import { defined } from "@digits-shared/helpers/filters"

const FORMATTER = new Intl.NumberFormat("en-US", {})
const LIST_FORMATTER = new Intl.ListFormat("en-US", {
  style: "long",
  type: "conjunction",
})
const VOWELS = ["a", "e", "i", "o", "u"]

// Intl.Segmenter is baseline for browsers
const graphemeSegmenter = new Intl.Segmenter("en", { granularity: "grapheme" })
const wordSegmenter = new Intl.Segmenter("en", { granularity: "word" })

// These regex patterns are baseline:
// https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Regular_expressions/Unicode_character_class_escape
const LEADING_PUNCTUATION_PATTERN = /^[\p{P}\p{Sc}\p{Sk}\p{Sm}]+/u

/**
 * Plain JSON-shaped data — the only inputs `stableKey` accepts. Encoded as a type so
 * non-serializable values (Date, Map, Set, class instances, functions, Symbol, BigInt)
 * fail at the call site instead of silently producing useless keys at runtime. Cycle
 * prohibition can't be expressed in the type system; that constraint is enforced at
 * runtime (see `stableKey`).
 */
export type StableKeyValue =
  | string
  | number
  | boolean
  | null
  | undefined
  | readonly StableKeyValue[]
  | { readonly [key: string]: StableKeyValue }

/**
 * Produces a deterministic string key from a value. Object keys are sorted, undefined fields
 * inside objects are dropped, and arrays preserve order. Suitable for cache/storage keys
 * derived from structured input.
 *
 * The {@link StableKeyValue} parameter type restricts inputs to plain JSON-shaped data.
 * Callers holding `unknown` or `Record<string, unknown>` (e.g., GraphQL variables,
 * `location.state`) must cast at the boundary — that cast is the point where the caller
 * commits to "this is plain serializable data".
 *
 * Top-level undefined and undefined array elements serialize to the literal string
 * `"undefined"` so they remain distinguishable from null (which serializes to `"null"`).
 *
 * Cyclic structures throw `RangeError` rather than recursing without bound — a cyclic input
 * indicates a caller bug, not a routine case to silently round-trip.
 *
 * @example
 * stableKey({ b: 2, a: 1 }) === stableKey({ a: 1, b: 2 })
 * stableKey({ a: 1, b: undefined }) === stableKey({ a: 1 })
 * stableKey(undefined) !== stableKey(null)
 */
export function stableKey(value: StableKeyValue): string {
  return stableStringify(value, new WeakSet<object>())
}

function stableStringify(value: StableKeyValue, ancestors: WeakSet<object>): string {
  if (value === undefined) return "undefined"
  if (value === null || typeof value !== "object") return JSON.stringify(value)
  if (ancestors.has(value)) {
    throw new RangeError("stableKey: cycle detected")
  }
  // Track the ancestor chain only — added on entry, removed on exit — so a DAG with shared
  // references serializes successfully and only true cycles throw.
  ancestors.add(value)
  try {
    if (Array.isArray(value)) {
      return `[${value.map((element) => stableStringify(element, ancestors)).join(",")}]`
    }

    // Array.isArray's type guard returns `any[]`, which doesn't subtract a readonly array
    // from the union — narrow explicitly so the index access below typechecks.
    const objectValue = value as { readonly [key: string]: StableKeyValue }

    return `{${Object.keys(objectValue)
      .filter((key) => objectValue[key] !== undefined)
      .sort()
      .map((key) => `${JSON.stringify(key)}:${stableStringify(objectValue[key], ancestors)}`)
      .join(",")}}`
  } finally {
    ancestors.delete(value)
  }
}

/**
 * Unicode-aware string manipulation utilities.
 */
export default {
  stableKey,

  pluralize(value: number, singular: string, plural: string) {
    const formattedValue = FORMATTER.format(value)
    const strs = (value === 1 ? singular : plural).split("{value}")
    return strs.join(formattedValue)
  },

  formatList(values: string[]) {
    return LIST_FORMATTER.format(values)
  },

  formatListToParts(values: string[]) {
    return LIST_FORMATTER.formatToParts(values)
  },

  makePossessive(value: string) {
    const lastCharacter = value.charAt(value.length - 1)
    return lastCharacter === "s" ? `${value}’` : `${value}’s`
  },

  escapeRegExp(value: string) {
    return value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")
  },

  containsSpecialCharacter(value: string) {
    const pattern = /^[A-Za-z0-9 ]+$/
    return !pattern.test(value)
  },

  displayUrl(fullUrl?: string | null) {
    if (!fullUrl) return undefined

    return fullUrl
      .replace(/^https?:\/\//, "")
      .replace(/^(www\.)?/, "")
      .replace(/\/$/, "")
  },

  httpAlwaysUrl(fullUrl?: string | null) {
    if (!fullUrl) return undefined

    let link = fullUrl
    if (!link.match(/^https?:\/\//)) {
      link = "http://".concat(link)
    }

    return link
  },

  truncate(value: string, maxLength: number, endingString = "…") {
    return value.length > maxLength ? value.substring(0, maxLength).trim() + endingString : value
  },

  truncateArray(values: string[], maxLength = 2, endingString = "etc") {
    if (values.length < 2) return values[0] || ""

    return `${[...values.slice(0, maxLength), values.length > maxLength ? endingString : undefined]
      .filter(defined)
      .join(", ")}.`
  },

  camelCaseToSpaces(value: string | undefined | null = "", uppercaseFirstLetter = false) {
    const camelCasedValue =
      value
        ?.replace(/([a-z])([A-Z])/g, "$1 $2")
        .replace(/&/g, " & ")
        .trim() || ""

    return uppercaseFirstLetter
      ? (camelCasedValue.charAt(0)?.toUpperCase() ?? "") + camelCasedValue.slice(1)
      : camelCasedValue
  },

  camelCase(value: string, uppercaseFirstLetter = false) {
    const camelCasedValue = value
      .replace(/([-_][a-z])/gi, ($1) => $1.toUpperCase().replace("-", "").replace("_", ""))
      .replace(/\s(.)/g, (ltr) => ltr.toUpperCase())
      .replace(/\s/g, "")

    if (uppercaseFirstLetter) return value.charAt(0).toUpperCase() + camelCasedValue.slice(1)

    return camelCasedValue.replace(/^(.)/, (ltr) => ltr.toLowerCase())
  },

  snakeCase(value: string) {
    return value.replace(/[\w]([A-Z])/g, (m) => `${m[0]}_${m[1]}`).toLowerCase()
  },

  kebabToSpaces(value = "", uppercaseFirstLetter = false) {
    const formattedValue =
      value
        ?.replace(/-+/g, " ") // Replace hyphens with spaces
        .trim() || ""

    return uppercaseFirstLetter
      ? (formattedValue.charAt(0).toUpperCase() ?? "") + formattedValue.slice(1).toLowerCase()
      : formattedValue
  },

  capitalize(value = "", lowercaseString = true) {
    const rest = value.slice(1)
    return value.charAt(0).toUpperCase() + (lowercaseString ? rest.toLowerCase() : rest)
  },

  titleCase(value: string) {
    const tokens = value.split(" ")
    return tokens.map((t) => this.capitalize(t)).join(" ")
  },

  uncapitalize(value: string) {
    return value.charAt(0).toLowerCase() + value.slice(1)
  },

  apostrophize(value?: string | null) {
    if (!value) return ""
    const trimmedValue = value.trim()
    const lastChar = trimmedValue[trimmedValue.length - 1]?.toLowerCase()
    return lastChar !== "s" ? `${trimmedValue}‘s` : `${trimmedValue}‘`
  },

  timezone(value?: string | null) {
    if (!value) return "-"
    return value.replace("/", " - ").replace("_", " ")
  },

  trimTrailingForwardSlashesIfPresent(value: string) {
    return value.replace(/\/+$/, "")
  },

  /**
   * Use heuristics to pull the first two initials from a name. If there are no
   * valid initials, it returns the original name.
   *
   * @example
   * initials("John Doe") // "JD"
   * initials("Javier de la Cruz") // "JD"
   *
   * @param name
   */
  initials(name: string): string {
    const words = [...wordSegmenter.segment(name)]
      .map(({ segment }) => segment.trim())
      .filter(Boolean)

    if (!words.length) return name

    const initials: string[] = []
    for (const word of words) {
      const cleanedWord = stripLeadingPunctuation(word)
      if (!cleanedWord) continue

      const cluster = this.firstGraphemeCluster(cleanedWord)
      if (cluster) {
        initials.push(cluster.toLocaleUpperCase())
      }

      if (initials.length === 2) break
    }

    if (!initials.length) return name

    return `${initials[0] ?? ""}${initials[1] ?? ""}`
  },

  /**
   * Get the first initial from a name (personal [given or family] or business).
   *
   * Under the hood, we're pulling the first user-perceived character (a
   * "grapheme cluster") from the string, instead of extracting the first byte.
   * That allows us to cover non-ASCII first initials (CJK characters, accented
   * characters like É, emoji, etc.)
   *
   * @example
   * firstInitial("John Doe") // "J"
   * firstInitial("John") // "J"
   * firstInitial("Doe") // "D"
   * firstInitial("Uber Eats") // "U"
   * firstInitial("") // undefined
   *
   * @param name
   */
  firstInitial(name: string): string | undefined {
    const words = [...wordSegmenter.segment(name)]
      .map(({ segment }) => segment.trim())
      .filter(Boolean)

    for (const word of words) {
      const cleanedWord = stripLeadingPunctuation(word)
      if (!cleanedWord) continue

      const firstCluster = this.firstGraphemeCluster(cleanedWord)

      if (firstCluster) {
        return firstCluster.toLocaleUpperCase()
      }
    }
  },

  /**
   * Get the first user-perceived character (a "grapheme cluster") from a string.
   *
   * User-perceived characters can be multiple bytes in UTF-8 or UTF-16 strings.
   * This function returns the first cluster of bytes that make one character;
   * for example, an emoji can be made of multiple other emojis (each being
   * multiple bytes). Naive byte-splitting returns the high surrogate code unit
   * for an emoji, splitting at the code point returns the first emoji, but
   * grapheme-splitting returns the combined emoji.
   *
   * This is also true of non-Latin characters, like "क्षे", which is made up
   * of four separate Unicode characters.
   *
   * @example
   * firstGraphemeCluster("👩‍👩‍👧‍👦 Family") // "👩‍👩‍👧‍👦"
   * Array.from("👩‍👩‍👧‍👦 Family").at(0) // "👩"
   * "👩‍👩‍👧‍👦 Family".at(0) // "\uD83D" (high surrogate code unit used for emoji)
   *
   * @param value
   */
  firstGraphemeCluster(value: string): string | undefined {
    const segments = graphemeSegmenter.segment(value)

    for (const { segment } of segments) {
      if (segment.trim() !== "") return segment
    }
  },

  firstChar(values: string) {
    const firstChar = values[0] || ""
    return parseInt(firstChar, 10) ? "#" : firstChar
  },

  getIndefiniteArticle(word: string) {
    if (VOWELS.includes(this.firstChar(word).toLowerCase())) {
      return "an"
    }

    return "a"
  },

  parseFloat(value: string, fixed?: number) {
    const parsedValue = parseFloat(value)
    if (isNaN(parsedValue)) return undefined
    return fixed === undefined ? parsedValue : parseFloat(parsedValue.toFixed(fixed))
  },

  /**
   * Combines multiple class name strings into a single space-separated string.
   * Falsy values (false, undefined, null) are filtered out.
   *
   * @example
   * classNames("foo", "bar")           // "foo bar"
   * classNames("foo", false, "bar")    // "foo bar"
   * classNames(isActive && "active")   // "active" or undefined
   * classNames(variant)                // variant value as class, e.g. "modal"
   */
  classNames(...classes: (string | false | undefined | null)[]) {
    const definedClasses = classes.filter(defined)
    return definedClasses.length ? definedClasses.join(" ") : undefined
  },

  simpleHashCode(value: string, offset = 0) {
    const strValue = value?.toString()
    if (typeof strValue !== "string") return 0
    return Math.max(
      0,
      strValue.split("").reduce((acc, char) => acc + (char.charCodeAt(0) + offset), 0)
    )
  },
}

function stripLeadingPunctuation(value: string): string {
  const trimmed = value.trimStart()
  return trimmed.replace(LEADING_PUNCTUATION_PATTERN, "")
}
